import { useState, useRef, useEffect, ReactNode } from "react";

// Helpers
import { getLongevity } from "@Common/helpers/Dates";
import { formatPhoneNumber } from "@Common/helpers/Modifiers/formatters";

// Components
import orgChartNodeTheme from "./orgChartNodeTheme";
import HolderImageUser from "@/assets/icons/svg/HolderImageUser";

// MUI
import { Card, Avatar, Typography, Collapse, IconButton, CardContent, Skeleton } from "@mui/material";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import CallIcon from "@mui/icons-material/Call";
import PersonalPhoneIcon from "@mui/icons-material/Smartphone";

// Styles
import "./style.css";
import { OrgChartNodeProps } from "@/Miscellaneous/interfaces/orgchart";

const PSEUDO_CLASS = "org-chart-hidden-pseudo-el-after";
const VAR_FONT_2 = "var(--font-family-v2)";

const checkForChildren = (data: OrgChartNodeProps["data"]) => {
	if (!data?.children) {
		return "leaf";
	}
	if (!data?.children.some((child) => child?.children)) {
		return "vertical";
	}

	return "";
};

function EmployeeAvatar({ data }: Readonly<Partial<OrgChartNodeProps>>) {
	let content;
	if (data?.root) {
		content = (
			<QuestionMarkIcon
				sx={{
					color: "#0071BC",
					fontSize: "3rem",
				}}
			/>
		);
	} else if (!data?.fullName) {
		content = <Skeleton animation="wave" variant="circular" width="100%" height="100%" />;
	} else if (data?.profilePicture) {
		content = (
			<img
				width={data.children ? orgChartNodeTheme.avatarStyle.width : orgChartNodeTheme.avatarStyleSmall.width}
				src={data?.profilePicture}
				crossOrigin="anonymous"
				alt=""
			/>
		);
	} else {
		content = <HolderImageUser />;
	}
	return <Avatar sx={data?.children ? orgChartNodeTheme.avatarStyle : orgChartNodeTheme.avatarStyleSmall}>{content}</Avatar>;
}

function EmployeeName({ data }: Readonly<Partial<OrgChartNodeProps>>) {
	let content: ReactNode = data?.fullName;
	if (data?.root) {
		content = <Skeleton animation={false} width={100} />;
	} else if (!data?.fullName) {
		content = <Skeleton animation="wave" variant="rectangular" width={100} height={10} />;
	}
	return (
		<Typography
			sx={{
				lineHeight: 1.2,
				fontWeight: "500",
				fontFamily: "var(--font-family-v1)",
				fontSize: "0.7rem",
			}}
			component="p"
			variant="body2"
		>
			{content}
		</Typography>
	);
}

function EmployeeJobTitle({ data }: Readonly<Partial<OrgChartNodeProps>>) {
	let content: ReactNode = data?.jobTitle;
	if (data?.root) {
		content = <Skeleton animation={false} width={70} />;
	} else if (!data?.jobTitle) {
		content = <Skeleton sx={{ marginTop: "2px" }} animation="wave" variant="rectangular" width={100} height={8} />;
	}
	return (
		<Typography
			sx={{
				lineHeight: 1.2,
				fontWeight: "500",
				color: "var(--text-color)",
				fontFamily: VAR_FONT_2,
			}}
			component="p"
			variant="caption"
		>
			{content}
		</Typography>
	);
}

export default function OrgChartNode({ data, expandAllCards = false, expandAllChart = false }: Readonly<OrgChartNodeProps>) {
	const [expanded, setExpanded] = useState(false);
	const [active, setActive] = useState(false);
	const collapseEl = useRef<HTMLDivElement>(null);

	const collapseNodeHandler = () => {
		setActive((currentValue) => !currentValue);
		collapseEl?.current?.classList.toggle(PSEUDO_CLASS);
	};

	let userEmail = "No Email address available";
	const communication = data?.communications?.find((comm) => comm.communicationType === "EMAIL" && comm.usage === "BUSINESS");
	if (!data?.root && communication) {
		userEmail = communication.value;
	}

	useEffect(() => {
		if (!data?.supervisorId) {
			collapseEl?.current?.classList?.add("org-chart-hidden-pseudo-el-before");
		}
		if (data?.supervisorId && data?.supervisorId !== "hidden-root") {
			setActive(expandAllChart);
			if (expandAllChart) {
				collapseEl?.current?.classList.add(PSEUDO_CLASS);
			} else {
				collapseEl?.current?.classList?.remove(PSEUDO_CLASS);
			}
		}
	}, [data?.supervisorId, expandAllChart]);

	useEffect(() => {
		setExpanded(expandAllCards);
	}, [expandAllCards]);

	return (
		<>
			{data?.payrollSourceId?.idValue !== "hidden" ? (
				<div ref={collapseEl} className="nodecontent">
					<Card className="nodecontent-card">
						{!data?.root ? (
							<IconButton
								sx={{
									zIndex: 2,
									position: "absolute",
									top: 0,
									right: 0,
									transform: `${expanded ? "rotate(180deg)" : "rotate(0deg)"}`,
								}}
								size="small"
								aria-label="collapse"
								onClick={() => setExpanded((currentValue) => !currentValue)}
							>
								<ExpandMoreIcon fontSize="small" />
							</IconButton>
						) : null}

						<EmployeeAvatar data={data} />

						<CardContent
							onClick={collapseNodeHandler}
							component="div"
							sx={{
								width: "100%",
								cursor: "pointer",
								userSelect: "none",
								padding: 0,
								"&:last-child": {
									paddingBottom: 0,
								},
								color: "var(--text-color)",
							}}
						>
							<div>
								<EmployeeName data={data} />

								<EmployeeJobTitle data={data} />
							</div>

							<Collapse in={expanded && !data?.root}>
								<Typography
									sx={{
										lineHeight: 1.2,
										marginTop: "0.1rem",
										fontFamily: VAR_FONT_2,
									}}
									component="p"
									variant="caption"
								>
									<strong>ID:</strong> {data?.employeeId}
								</Typography>

								{data?.communications?.map((comm) => {
									if (comm?.communicationType === "PHONE") {
										return (
											<Typography
												key={comm._id}
												sx={{
													lineHeight: 1.2,
													fontFamily: VAR_FONT_2,
													display: "flex",
													alignItems: "center",
												}}
												component="p"
												variant="caption"
											>
												{comm?.usage === "PERSONAL" && (
													<PersonalPhoneIcon
														sx={{
															fontSize: "0.9rem",
															marginRight: "0.3rem",
														}}
													/>
												)}

												{comm?.usage === "BUSINESS" && (
													<CallIcon
														sx={{
															fontSize: "0.9rem",
															marginRight: "0.3rem",
														}}
													/>
												)}
												{comm?.value.includes("ext")
													? formatPhoneNumber(comm?.value, true)
													: formatPhoneNumber(comm?.value, false)}
											</Typography>
										);
									}
									return <></>;
								})}

								<Typography
									sx={{
										lineHeight: 1.2,
										fontFamily: VAR_FONT_2,
										display: "flex",
										alignItems: "center",
									}}
									component="p"
									variant="caption"
								>
									<EmailIcon
										sx={{
											fontSize: "0.9rem",
											marginRight: "0.3rem",
										}}
									/>
									{userEmail}
								</Typography>

								<Typography
									sx={{
										lineHeight: 1.2,
										fontFamily: VAR_FONT_2,
										display: "flex",
										alignItems: "center",
									}}
									component="p"
									variant="caption"
								>
									<AccessTimeIcon
										sx={{
											fontSize: "0.9rem",
											marginRight: "0.3rem",
										}}
									/>
									{getLongevity(data?.hireDate)}
								</Typography>
							</Collapse>
						</CardContent>
					</Card>
				</div>
			) : null}

			<ul className={active ? "org-chart-hidden" : ""}>
				{data?.children?.map((child, i) => (
					<li key={`org-chart-node-${i + 1}`} className={checkForChildren(child)}>
						<OrgChartNode expandAllCards={expandAllCards} expandAllChart={expandAllChart} data={child} />
					</li>
				))}
			</ul>
		</>
	);
}
